import React, { useSyncExternalStore } from 'react';
import { useLocation } from 'react-router-dom';
import { applyUpdate, getWaiting, subscribe } from '../utils/swUpdate';

/**
 * UpdateBanner - tells the user a new version of the app is ready.
 *
 * After a deploy the installed app kept running the old bundle until it had
 * been closed twice: the new service worker waited for every window to close.
 * serviceWorkerRegistration.js now checks for a new worker on every open and
 * every return to the foreground, and this banner appears when one is waiting.
 *
 * Tapping it is the only way to update. There is no timer, no automatic
 * reload and no dismiss button, so a driver halfway through a form loses
 * nothing until they choose to tap.
 *
 * It sits at the bottom so it never stacks with OfflineBanner at the top. The
 * portal installs as "My Snow Service", so its wording leaves out the name.
 */
function UpdateBanner() {
  const waiting = useSyncExternalStore(subscribe, getWaiting, getWaiting);
  const { pathname } = useLocation();

  if (!waiting) return null;

  const isPortal = pathname === '/portal' || pathname.startsWith('/portal/');
  const text = isPortal
    ? 'A new version is ready. Tap to update.'
    : 'A new version of SnowTrack is ready. Tap to update.';

  return (
    <div className="update-banner" role="status" aria-live="polite">
      <button type="button" className="update-banner-button" onClick={applyUpdate}>
        {text}
      </button>

      <style>{`
        .update-banner {
          position: fixed;
          left: 0;
          right: 0;
          bottom: 0;
          z-index: 2000;
          background: #1e88e5;
          box-shadow: 0 -1px 4px rgba(0, 0, 0, 0.2);
        }

        .update-banner-button {
          display: block;
          width: 100%;
          min-height: 44px;
          padding: 12px 16px;
          /* Clears the home indicator on a standalone iOS launch. */
          padding-bottom: calc(12px + env(safe-area-inset-bottom, 0px));
          background: transparent;
          border: none;
          color: #fff;
          font-size: 0.95rem;
          font-weight: 600;
          line-height: 1.3;
          text-align: center;
          cursor: pointer;
        }
      `}</style>
    </div>
  );
}

export default UpdateBanner;
